/**
 * Claude (Anthropic) LLM Service
 * Provider-specific implementation for all 4 generation functions.
 * Uses Anthropic SDK via CORS proxy.
 */

import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import { HookhouseFormData, ParsedSunoOutput, MusicCritique, SunoClip } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { trackUsage } from '@/utils/costTracker';
import {
  getClaudeKey, getClaudeProxy, getClaudeModel,
  parseResponse, buildSunoPromptInput, buildRegenerateInput,
  buildImagePrompt, buildCritiquePrompt, parseCritiqueResponse,
  enforceLyricsLimit,
  MUSIC_CRITIQUE_JSON_INSTRUCTION,
} from './llmShared';
import type { RegenerateOptions } from './llmShared';

async function callClaude(systemPrompt: string, userPrompt: string, temperature: number, maxTokens: number): Promise<{ text: string; inputTokens: number; outputTokens: number }> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error("Claude API key not configured. Please set your API key in Settings.");
  const model = getClaudeModel();
  console.log(`[Claude] Calling API with model: ${model}`);

  const { text, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey,
    baseURL: getClaudeProxy(),
    model,
    systemPrompt,
    userPrompt,
    temperature,
    maxTokens,
  });

  return { text, inputTokens, outputTokens };
}

export const generateSunoPrompt = async (
  data: HookhouseFormData,
  vocalArchitecture?: string,
  productionArchitecture?: string,
  riffDnaArchitecture?: string,
  soloArchitecture?: string,
  narrativeArchitecture?: string,
  personaOverride?: PersonaOverride | null
): Promise<ParsedSunoOutput> => {
  const input = buildSunoPromptInput(data, vocalArchitecture, productionArchitecture, riffDnaArchitecture, soloArchitecture, narrativeArchitecture, personaOverride);

  try {
    const { text, inputTokens, outputTokens } = await callClaude(input.systemPrompt, input.userPrompt, 0.85, 8000);
    trackUsage({ provider: 'claude', model: getClaudeModel(), feature: 'generation', inputTokens, outputTokens });
    if (!text) throw new Error("No response from Claude.");
    const parsed = parseResponse(text, input.styleStrength, input.weirdness);
    return await enforceLyricsLimit(parsed, callClaude);
  } catch (error: unknown) {
    console.error("Claude API Error:", error);
    throw new Error(error instanceof Error ? error.message : "Unknown error generating prompt. Check your API key.");
  }
};

export const regenerateWithAdvice = async (originalOutput: ParsedSunoOutput, critique: MusicCritique, opts?: RegenerateOptions): Promise<ParsedSunoOutput> => {
  const input = buildRegenerateInput(originalOutput, critique, opts);

  try {
    const { text, inputTokens, outputTokens } = await callClaude(input.systemPrompt, input.userPrompt, 0.8, 8000);
    trackUsage({ provider: 'claude', model: getClaudeModel(), feature: 'generation', inputTokens, outputTokens });
    if (!text) throw new Error("No response from Claude.");
    return parseResponse(text, input.styleStrength, input.weirdness);
  } catch (error: unknown) {
    throw new Error(`Regeneration Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const generateImageBlueprint = async (output: ParsedSunoOutput): Promise<string> => {
  const { systemPrompt, userPrompt } = buildImagePrompt(output);

  try {
    const { text, inputTokens, outputTokens } = await callClaude(
      systemPrompt + "\n\nRespond in JSON only, no markdown wrapping.",
      userPrompt, 0.7, 4000
    );
    trackUsage({ provider: 'claude', model: getClaudeModel(), feature: 'artwork', inputTokens, outputTokens });
    // Extract JSON from potential markdown wrapping
    const jsonMatch = (text || '').match(/\{[\s\S]*\}/);
    return jsonMatch ? jsonMatch[0] : text || "";
  } catch (error: unknown) {
    throw new Error(`Blueprint Generation Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const generateMusicCritique = async (clip: SunoClip): Promise<MusicCritique> => {
  const { systemPrompt, userPrompt } = buildCritiquePrompt(clip);

  try {
    const { text, inputTokens, outputTokens } = await callClaude(
      systemPrompt, userPrompt + MUSIC_CRITIQUE_JSON_INSTRUCTION, 0.75, 4000
    );
    trackUsage({ provider: 'claude', model: getClaudeModel(), feature: 'critic', inputTokens, outputTokens });
    if (!text) throw new Error("No response from Claude for critique.");
    return parseCritiqueResponse(text, clip.id);
  } catch (error: unknown) {
    console.error("Critique Error:", error);
    throw new Error(`Critique Generation Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
